import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { PageChrome } from "../components/PageChrome";
import { VerseCollection } from "../components/VerseCollection";
import {
  articleJsonLd,
  breadcrumbJsonLd,
  canonical,
  documentHeaders,
  faqJsonLd,
  socialMeta,
  verseOgImage,
} from "../lib/seo";
import { versePath, verseTopicBySlug } from "../lib/verses";

/**
 * `/verses/<topic>` — one collection.
 *
 * No `Quiz`-style special vocabulary here and no `ItemList`: the page is a
 * piece of writing with ten quotations in it, and `BlogPosting` is what it is.
 * Marking a page of scripture up as a product listing or a how-to would be the
 * usual mistake of reaching for whichever schema has the prettiest rich result.
 */
export const Route = createFileRoute("/verses/$slug")({
  loader: ({ params }) => {
    const topic = verseTopicBySlug(params.slug);
    if (!topic) throw notFound();
    return topic;
  },
  headers: documentHeaders,
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const path = versePath(loaderData.slug);
    return {
      meta: [
        { title: loaderData.title },
        { name: "description", content: loaderData.description },
        ...socialMeta({
          title: loaderData.title,
          description: loaderData.description,
          path,
          lang: "en",
          image: verseOgImage(loaderData.slug),
          imageAlt: loaderData.h1,
        }),
        {
          "script:ld+json": articleJsonLd({
            headline: loaderData.h1,
            description: loaderData.description,
            path,
            published: loaderData.published,
            updated: loaderData.updated,
          }),
        },
        {
          "script:ld+json": breadcrumbJsonLd([
            { name: "Bible Shield", path: "/" },
            { name: "Verses", path: "/verses" },
            { name: loaderData.h1, path },
          ]),
        },
        ...(loaderData.faq
          ? [{ "script:ld+json": faqJsonLd(loaderData.faq) }]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical(path) }],
    };
  },
  component: VersesPage,
});

function VersesPage() {
  const topic = Route.useLoaderData();
  return (
    <PageChrome>
      <nav
        aria-label="breadcrumb"
        className="mx-auto mb-6 w-full max-w-[68ch] text-sm text-ink2"
      >
        <Link
          to="/verses"
          className="underline decoration-hair underline-offset-4 transition-colors hover:text-brand"
        >
          verses
        </Link>
      </nav>
      <VerseCollection topic={topic} />
    </PageChrome>
  );
}
